import { Fragment, FunctionComponent, ReactElement, useEffect, useMemo, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useLevelLocations } from '@/queries/locations';
import { Location } from '@/core/entities';
import UpdateLocationForm, {
        UpdateLocationFormModel,
} from '@/features/locations/UpdateLocationForm';
import RootLocationManagement from '@/features/locations/locations-management/RootLocationsManagement';
import Backdrop from '@/components/ui/backdrop';
import { SiteContext } from '../../index/Site';

const LocationsPage: FunctionComponent = (): ReactElement => {
        const queryClient = useQueryClient();
        const navigate = useNavigate();
        const { site } = useOutletContext<SiteContext>();

        const rootLevel = useMemo(() => site?.levels.find((level) => !level.parent?.id), [site]);

        const [updateLocationForm, setUpdateLocationForm] = useState<{
                open: boolean;
                formModel: UpdateLocationFormModel;
                location?: Location;
        }>({
                open: false,
                formModel: { name: '' },
        });

        const [locations, setLocations] = useState<Location[]>([]);

        const { queryKey: levelLocationsQueryKey, query: levelLocationsQuery } = useLevelLocations({
                level: rootLevel,
                queryParams: { limit: 99999 },
        });

        const {
                isLoading: isLevelLocationsLoading,
                data: levelLocationsData,
        } = levelLocationsQuery;
        const { pages } = levelLocationsData || {};

        const refetchLocations = async () => {
                await queryClient.invalidateQueries(levelLocationsQueryKey);
        };

        useEffect(() => {
                const { locations } = pages?.[0] || {};
                if (locations) {
                        setLocations(locations.data);
                }
        }, [pages]);

        if (!rootLevel || !site) return <Backdrop />;

        return (
                <Fragment>
                        <RootLocationManagement
                                site={site}
                                level={rootLevel}
                                header={{
                                        controlLocationsTemplate: true,
                                }}
                                table={{
                                        data: locations,
                                        isLoading: isLevelLocationsLoading,
                                        rowAction: (location) => navigate(`/sites/${site.id}/locations/${location.id}`),
                                        columnActions: {
                                                edit: (location) =>
                                                        setUpdateLocationForm({
                                                                ...updateLocationForm,
                                                                location,
                                                                formModel: { name: location.name },
                                                                open: true,
                                                        }),
                                        },
                                }}
                                invalidateData={refetchLocations}
                        />
                        <UpdateLocationForm
                                open={updateLocationForm.open}
                                onClose={() => setUpdateLocationForm({ ...updateLocationForm, open: false })}
                                location={updateLocationForm.location}
                                level={rootLevel}
                                formModel={updateLocationForm.formModel}
                                onChangeFormModel={(field: keyof Location, value: string) =>
                                        setUpdateLocationForm({
                                                ...updateLocationForm,
                                                formModel: { ...updateLocationForm.formModel, [field]: value },
                                        })
                                }
                                onSubmitted={refetchLocations}
                        />
                </Fragment>
        );
};

export default LocationsPage;
